
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import LoaderSpinner from "~/components/LoaderSpinner.vue";
import Validation from "~/entities/Validation";
import ValidationTracking from "~/entities/ValidationModules/ValidationTracking";

interface DelayI {
  fromType: number;
  from: string;
  toType: number;
  to: string;
  targetType: number;
  target: string;
  markupType: number;
  markup: number;
  active: boolean;
}

export default Vue.extend({
  name: "TrackingPage",
  components: { LoaderSpinner },
  beforeRouteLeave(to, from, next) {
    if (to && to.name === "ship" && typeof window !== "undefined") {
      this.clearActiveNotices();
      this.flushShipStore("ship"); // call payment flusher too
      this.flushPaymentStore("ship");
    } else if (
      to &&
      to.name === "event-shipping" &&
      typeof window !== "undefined"
    ) {
      this.clearActiveNotices();
      this.flushShipStore("event"); // call payment flusher too
      this.flushPaymentStore("event");
    }
    next();
  },
  data() {
    return {
      title:
        "Track Your Bicycle | Bike Tracking | Bike Tracker | Bikeflights.com",
      render: true,
      submited: false,
      tracking: "" as string,
      error: false,
      isScanOpen: false,
      validation: new Validation(new ValidationTracking()),
      accordionOpen: {
        international: false as boolean,
        national: false as boolean,
        city: false as boolean,
        postal: false as boolean
      } as { [key: string]: boolean },
      delays: {} as { 
        international?: {[key: number]: DelayI[]}, 
        national?: {[key: number]: DelayI[]},
        city?: {[key: number]: DelayI[]},
        postal?: {[key: number]: DelayI[]}
      } as { [key: string]: { [key: number]: DelayI[] } },
      
    }
  },
  head() {
    return {
      title: this.title,
    };
  },
  computed: {
    // Vuex getters
    ...mapGetters({
      loading: "loading",
      packageLocation: "packageLocation",
      activeNotices: "errorsBag/activeNotices" as any,
      alertResponses: "errorsBag/alertResponses" as any,
    }),
    hasShipDate(): boolean {
      return (
        (this as any).packageLocation.shipped === true &&
        !!(this as any).packageLocation.shipDate &&
        (this as any).packageLocation.shipDate !== "0001-01-01T00:00:00"
      );
    },
    labelCreatedScan(): any {
      const scans = (this as any).packageLocation.scans;
      if (!scans || !scans.length) return null;
      // scans are newest-first, so the last "M" (manifest / label created) scan is the earliest
      for (let i = scans.length - 1; i >= 0; i--) {
        if (scans[i].activityStatusType === "M") return scans[i];
      }
      return null;
    },
    shipLegendDate(): string {
      if ((this as any).hasShipDate) {
        return (this as any).formatDate((this as any).packageLocation.shipDate);
      }
      if ((this as any).labelCreatedScan) {
        // timeStamp is the scan-location wall-clock time tagged as UTC, same as the scan list below
        return (this as any).formatDate((this as any).labelCreatedScan.timeStamp);
      }
      return "Not shipped yet";
    },
  },
  watch: {
    packageLocation: {
      deep: true,
      immediate: false,
      handler(newVal) {
        if (newVal && newVal.success) {
          if (newVal.scans.length) {
            (this as any).isScanOpen = false;
          }
          this.addQueryParam("trackingnumber", (this as any).tracking);
        } else if ((this as any).tracking) {
          (this as any).error = true;
          (this as any).setError(newVal, "trackingNumber");
        }
      },
    },
  },
  async mounted() {
    const acc = document.getElementsByClassName("btn-accordion");
    for (let i = 0; i < acc.length; i++) {
      acc[i].addEventListener("click", function () {
        acc[i].classList.toggle("active");
      });
    }
    (this as any).clearActiveNotices();
    (this as any).tracking =
      this.$route.params && this.$route.params.trackingnumber
        ? this.$route.params.trackingnumber
        : this.$route.query && this.$route.query.trackingnumber
        ? this.$route.query.trackingnumber
        : "";
    (this as any).submited = false;
    (this as any).error = false;
    (this as any).validation.module.component = this;
    if ((this as any).tracking) {
      await (this as any).checkTracking();
    }
    window.addEventListener("popstate", () => {
      (this as any).tracking =
        this.$route.params && this.$route.params.trackingnumber
          ? this.$route.params.trackingnumber
          : this.$route.query && this.$route.query.trackingnumber
          ? this.$route.query.trackingnumber
          : "";
      (this as any).submited = false;
      (this as any).error = false;
      (this as any).validation.module.component = this;
      if ((this as any).tracking) {
        (this as any).checkTracking();
      }
    });
  },
  async created() {
    try {
      const served = await this.$axios.$get(`/api/PackageLocation/delays`, {withCredentials: false});
      if(served){
        this.delays = served.reduce((acc, item) => {
          const typeMap = {
            1: 'international',
            2: 'national',
            4: 'city',
            8: 'postal'
          };
          const typeKey = typeMap[item.toType];
          if (typeKey) {
            acc[typeKey] = acc[typeKey] || {};
            acc[typeKey][item.markup] = acc[typeKey][item.markup] || [];
            acc[typeKey][item.markup].push(item);
          }
          return acc;
        }, {});
      }
    }catch (e) {      
      return false;
    }
  },
  methods: {
    ...mapMutations({
      setPackageLocation: "setPackageLocation",
      setLoading: "setLoading",
      flushShipStore: "ship/flush",
      flushPaymentStore: "payment/flush",
    }),
    ...mapActions({
      getTrackPackageLocation: "getTrackPackageLocation",
      requestAlert: "errorsBag/requestAlert",
      clearActiveNotices: "errorsBag/clearActiveNotices",
      clearAlertResponses: "errorsBag/clearAlertResponses",
      removeNotice: "errorsBag/removeNotice",
      addNotice: "errorsBag/addNotice",
    }),
    formatDate(date: any, daysDelta = 0, withTime = false): any {
      if (typeof date === "string" || date instanceof String) {
        date = new Date(date as string);
      }
      if (daysDelta) {
        let increment, skips;
        const auxDate = new Date(date.getTime());
        for (
          increment = 0, skips = 0;
          increment < daysDelta && skips < 31;
          auxDate.setDate(auxDate.getDate() + 1)
        ) {
          if ((this as any).getDisabledDates(auxDate)) {
            skips++;
          } else {
            increment++;
          }
          if (increment === daysDelta) break;
        }
        date = new Date(auxDate.getTime());
      }
      let formattedDate = "";
      if (withTime) {
        formattedDate = date.toLocaleDateString("en-us", {
          year: "numeric",
          weekday: "short",
          month: "short",
          day: "numeric",
          timeZone: "utc",
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        });
      } else {
        formattedDate = date.toLocaleDateString("en-us", {
          year: "numeric",
          weekday: "short",
          month: "short",
          day: "numeric",
          timeZone: "utc",
        });
      }
      return formattedDate;
    },
    getDisabledDates(date: any): boolean {
      if (typeof date === "string" || date instanceof String)
        date = new Date(date as string);
      if (date.getFullYear() > (this as any).currentYear + 1)
        // disable if a date is a year in the future to improve performance
        return true;
      if (!(date.getDay() % 6)) return true;
      if ((this as any).isBeforeToday(date)) return true;
      return (this as any).isADayOff(date);
    },
    isADayOff(date): boolean {
      if ((this as any).Holidays && (this as any).Holidays.length < 1) {
        (this as any).Holidays = [
          // today I learned that in this particular form of the Date constructor, the months start at 0 for no reason, while the other components don't.
          new Date(2023, 1 - 1, 1),
          new Date(2023, 1 - 1, 2),
          new Date(2023, 5 - 1, 29),
          new Date(2023, 7 - 1, 4),
          new Date(2023, 9 - 1, 4),
          new Date(2023, 11 - 1, 23),
          new Date(2023, 12 - 1, 25),
          new Date(2024, 1 - 1, 1),
        ];
      }
      for (let index = 0; index < (this as any).Holidays.length; index++) {
        if ((this as any).Holidays[index].getTime() === date.getTime()) {
          return true;
        }
      }
      return false;
    },
    // TODO: this function is repeated in many places, need to be in a service / package / store, whatever is best practice in VUE I did refactor of these functions in Costs.vue
    isBeforeToday(date: any): any {
      if (typeof date === "string" || date instanceof String) {
        date = new Date(date as string);
      }
      const today = (this as any).getToday();
      date = (this as any).dateToISO(date);
      return date < today;
    },
    // TODO: this function is repeated in many places, need to be in a service / package / store, whatever is best practice in VUE I did refactor of these functions in Costs.vue
    getToday(): any {
      return this.dateToISO(new Date());
    },
    // TODO: this function is repeated in many places, need to be in a service / package / store, whatever is best practice in VUE I did refactor of these functions in Costs.vue
    dateToISO(date: any): any {
      if (typeof date === "string" || date instanceof String) {
        date = new Date(date as string);
      }
      if (date instanceof Date) {
        const offsetMs = date.getTimezoneOffset() * 60 * 1000;
        const msLocal = date.getTime() - offsetMs;
        const dateLocal = new Date(msLocal);
        const iso = dateLocal.toISOString();
        const isoLocal = iso.slice(0, 10);
        return isoLocal;
      }
      return false;
    },
    async checkTracking() {
      try {
        this.setLoading(true);
        (this as any).error = false;
        if (
          (this as any).tracking &&
          (await (this as any).validation.Validate())
        ) {
          this.submited = true;
          await (this as any).getTrackPackageLocation((this as any).tracking);
        } else {
          (this as any).error = true;
        }
        this.setLoading(false);
      } catch (e) {
        this.setError(this.tracking, "trackingNumber");
        this.error = true;
        this.setLoading(false);
      }
    },
    addQueryParam(key, value) {
      // Navigate to the same route with the updated query parameters
      if ((this as any).$route.query) {
        (this as any).$route.query[key] = value;
      }
      if (window) {
        const url = new URL(window.location.href);
        url.searchParams.delete(key);
        url.searchParams.set(key, value);
        window.history.replaceState(null, null as any, url as any);
      }
    },
    resetSubmit() {
      this.submited = false;
    },
    toggleDropDown() {
      (this as any).render = false;
      (this as any).isScanOpen = !(this as any).isScanOpen;
      (this as any).render = true;
    },
    reloadTracking():void {
      (this as any).error = false;
      (this as any).validation.module.resetValidations();
      (this as any).setPackageLocation({});
      (this as any).tracking = "";
      (this as any).$router.push({ path: "/tracking", query: {} });
      // window.location.reload();
    },
    goToClaims():void {
      (this as any).error = false;
      (this as any).validation.module.resetValidations();
      (this as any).setPackageLocation({});
      (this as any).tracking = "";
      (this as any).$router.push("/claims");
    },
    setError(e, type: any = ""):void {
      this.validation.module.requestAlert({ event: e, type });
    },
    clearError(e, type: any = ""):void {
      this.validation.module.removeNotice({ event: { direction: e }, type });
    },
    getProgress(): number {
      if (
        !(
          (this as any).packageLocation &&
          (this as any).packageLocation.progressLevel
        )
      )
        return 0;
      const progress = Number((this as any).packageLocation.progressLevel);
      return progress > 0 ? (progress > 100 ? 100 : progress) : 0;
    },
    toggleAccordion(section:string):void {
      this.accordionOpen[section] = !this.accordionOpen[section] as boolean;
    },
  },
});
